// What the sender adds to a signing email before it goes (Documents tab
// PR 8b, "Before it goes": docs/design/esign-send-confirmation.md, Nate
// Swartz 2026-09-19).
//
// CONSUMER: workers/api/src/routes/esign.ts (the invitation email),
// shared/db/esign-reminders.ts (reminders repeat the note and video), and
// the Review & send window (the same default subject it shows).
//
// Three things, each small and each checked:
//   * the SUBJECT the sender typed (or the default this module builds);
//   * the NOTE, rendered with its links as links (Nate pastes Loom links,
//     and a link that arrives as text is one the client can't click);
//   * a VIDEO: a Loom share link, shown as a card above the sign button.
//     The OS holds a link and what Loom says about it, never the video.
//
// Everything here is pure except `fetchLoomDetails`, which takes its fetch
// as a dependency so a test never reaches loom.com.

/** Longest subject a sender may set. Email clients cut far sooner. */
export const MAX_SIGNING_SUBJECT_LENGTH = 200;

/** A signing video as stored on the envelope and drawn in the email. */
export interface SigningVideo {
  url: string;
  /** What Loom says the video is called; null when Loom didn't answer. */
  title: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** A document's display name from its filename ("SPDS_2286.pdf" → "SPDS 2286"). */
export function documentDisplayName(filename: string): string {
  return filename.replace(/\.[a-z0-9]{2,5}$/i, "").replace(/[-_]+/g, " ").trim() || filename;
}

/**
 * The subject the Review & send window starts with: "Your {document} for
 * {address} is ready to sign", or without the address when there isn't
 * one. A package of several documents names the first and counts the rest.
 * Prefilled, never locked: the sender edits it freely.
 */
export function defaultSigningSubject(input: {
  documentNames: readonly string[];
  propertyAddress?: string | null;
}): string {
  const names = input.documentNames.map(documentDisplayName).filter(Boolean);
  const first = names[0] ?? "document";
  const what = names.length > 1 ? `${first} and ${names.length - 1} more` : first;
  const where = input.propertyAddress?.trim() ? ` for ${input.propertyAddress.trim()}` : "";
  return `Your ${what}${where} ${names.length > 1 ? "are" : "is"} ready to sign`.slice(
    0,
    MAX_SIGNING_SUBJECT_LENGTH,
  );
}

/** A subject from a request body: trimmed, one line, capped; null when
 *  empty (the caller keeps its own default). */
export function cleanSigningSubject(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.replace(/\s+/g, " ").trim().slice(0, MAX_SIGNING_SUBJECT_LENGTH);
  return s.length > 0 ? s : null;
}

const URL_PATTERN = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/gi;

/** Make every http(s) link in ALREADY-ESCAPED text clickable. */
export function linkifyEscaped(escapedText: string): string {
  return escapedText.replace(URL_PATTERN, (url) => `<a href="${url}">${url}</a>`);
}

/**
 * The sender's note as email HTML: escaped, line breaks kept, and every
 * http(s) link made clickable. Escaping happens first and links are found
 * in the escaped text, so nothing the sender types can become markup.
 */
export function noteToHtml(note: string | null | undefined): string {
  const text = (note ?? "").trim();
  if (!text) return "";
  return text
    .split(/\n{2,}/)
    .map((para) => {
      const html = linkifyEscaped(escapeHtml(para));
      return `<p>${html.replace(/\n/g, "<br>")}</p>`;
    })
    .join("");
}

/** A Loom share or embed link, normalised to its share form; null for
 *  anything else. Only Loom, by design: the card is drawn from what Loom
 *  says about the video, so another host would be a link we can't vouch
 *  for. */
export function normaliseLoomUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (url.hostname !== "www.loom.com" && url.hostname !== "loom.com") return null;
  const m = url.pathname.match(/^\/(share|embed)\/([A-Za-z0-9]{8,64})\/?$/);
  if (!m) return null;
  return `https://www.loom.com/share/${m[2]}`;
}

/** "3:12" from seconds. */
export function formatVideoDuration(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Ask Loom for a share link's public details (its oEmbed endpoint).
 * Best-effort: any failure returns the link with no details, and the
 * email then shows a plain "Watch the video" line instead of a card.
 */
export async function fetchLoomDetails(
  shareUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SigningVideo> {
  const bare: SigningVideo = { url: shareUrl, title: null, durationSeconds: null, thumbnailUrl: null };
  try {
    const res = await fetchImpl(
      `https://www.loom.com/v1/oembed?url=${encodeURIComponent(shareUrl)}`,
      { signal: AbortSignal.timeout(4000) },
    );
    if (!res.ok) return bare;
    const body = (await res.json()) as { title?: unknown; duration?: unknown; thumbnail_url?: unknown };
    const thumb = typeof body.thumbnail_url === "string" ? body.thumbnail_url : null;
    return {
      url: shareUrl,
      title: typeof body.title === "string" ? body.title.slice(0, 200) : null,
      durationSeconds: typeof body.duration === "number" ? body.duration : null,
      // Only an https image goes in an email.
      thumbnailUrl: thumb && thumb.startsWith("https://") ? thumb : null,
    };
  } catch {
    return bare;
  }
}

/**
 * The video card, placed above the sign button. Table-based with inline
 * styles because email clients ignore classes; hex literals because email
 * clients have no tokens. Without Loom's details it is one plain line.
 */
export function videoCardHtml(video: SigningVideo | null | undefined): string {
  if (!video) return "";
  const url = escapeHtml(video.url);
  const title = video.title ? escapeHtml(video.title) : null;
  const length = formatVideoDuration(video.durationSeconds);
  if (!video.thumbnailUrl) {
    return `<p>Watch the video first: <a href="${url}">${title ?? url}</a>${length ? ` (${length})` : ""}</p>`;
  }
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:12px 0;border:1px solid #e0e6ec;border-radius:10px;">` +
    `<tr><td style="padding:10px;">` +
    `<a href="${url}"><img src="${escapeHtml(video.thumbnailUrl)}" width="280" alt="${title ?? "Video"}" style="display:block;border-radius:6px;border:0;max-width:100%;"/></a>` +
    `<p style="margin:8px 0 0;font-family:Arial,sans-serif;font-size:14px;">` +
    `<a href="${url}" style="color:#022c51;font-weight:bold;text-decoration:none;">&#9654; ${title ?? "Watch the video"}</a>` +
    (length ? `<span style="color:#555555;"> · ${length}</span>` : "") +
    `</p></td></tr></table>`
  );
}

/** The same in plain text, for a reminder's text body. */
export function videoLineText(video: SigningVideo | null | undefined): string {
  if (!video) return "";
  const length = formatVideoDuration(video.durationSeconds);
  return `Watch the video first: ${video.title ? `${video.title} ` : ""}${video.url}${length ? ` (${length})` : ""}`;
}
